import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppRoutingModule } from './app-routing.module';
import { CookieService } from 'ngx-cookie-service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { registerLocaleData } from '@angular/common';
import localeDeCh from '@angular/common/locales/de-CH';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SharedModule } from './shared/shared.module';
import { OrdersComponent } from './orders/orders.component';
import { DocumentsComponent } from './documents/documents.component';
import { MasterDataComponent } from './master-data/master-data.component';
import { UserDataComponent } from './user-data/user-data.component';
import { CustomerAccountComponent } from './customer-account/customer-account.component';
import { BlocklistComponent } from './blocklist/blocklist.component';
import { SettingsComponent } from './settings/settings.component';
import { ManagementComponent } from './management/management.component';
import { PostalCodeModule } from './postal-code/postal-code.module';
import { NavigationComponent } from './core/components/navigation/navigation.component';
import { AppDateAdapter, APP_DATE_FORMATS } from '#date-adapter';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatBadgeModule } from '@angular/material/badge';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MatNativeDateModule, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MediDialogComponent } from './medi-dialog/medi-dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule } from '@angular/material/snack-bar';

import { MatListModule } from '@angular/material/list';
import { DialogAbsenceComponent } from './dialogs/dialog-absence/dialog-absence.component';
import { DialogTaskComponent } from './dialogs/dialog-task/dialog-task.component';
import { DialogQueryComponent } from './dialogs/dialog-query/dialog-query.component';
import { OrderDetailComponent } from './order-detail/order-detail.component';
import { DragDropDirective } from './drag-drop.directive';

import { ScrollingModule } from '@angular/cdk/scrolling';
import { DialogDoctorComponent } from './dialogs/dialog-doctor/dialog-doctor.component';

import { CanDeactivateGuard } from './can-deactivate-guard.service';
import { MatPaginatorModule } from '@angular/material/paginator';
import { ReportingComponent } from './reporting/reporting.component';

registerLocaleData(localeDeCh, 'de-CH');

// AoT requires an exported function for factories
export function HttpLoaderFactory(httpClient: HttpClient) {
    return new TranslateHttpLoader(httpClient, './assets/i18n/', '.json');
}

@NgModule({
    declarations: [
        AppComponent,
        OrdersComponent,
        DocumentsComponent,
        MasterDataComponent,
        UserDataComponent,
        CustomerAccountComponent,
        BlocklistComponent,
        SettingsComponent,
        ManagementComponent,
        NavigationComponent,
        MediDialogComponent,
        DialogAbsenceComponent,
        DialogTaskComponent,
        DialogQueryComponent,
        OrderDetailComponent,
        DragDropDirective,
        DialogDoctorComponent,
        ReportingComponent,
    ],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        FormsModule,
        ReactiveFormsModule,
        BrowserAnimationsModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }),
        CoreModule,
        AppRoutingModule,
        SharedModule,
        PostalCodeModule,
        MatButtonModule,
        MatCardModule,
        MatIconModule,
        MatTableModule,
        MatPaginatorModule,
        MatTooltipModule,
        MatTabsModule,
        MatBadgeModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatSelectModule,
        MatToolbarModule,
        MatDialogModule,
        MatDividerModule,
        MatMenuModule,
        MatListModule,
        MatRadioModule,
        MatSortModule,
        MatProgressBarModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        ScrollingModule,
    ],
    providers: [
        CookieService,
        CanDeactivateGuard,
        {
            provide: DateAdapter, useClass: AppDateAdapter
        },
        {
            provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS
        },
        provideHttpClient(withInterceptorsFromDi())
    ]
})
export class AppModule {
}
